<template>
  <div>
    <b-row>
      <b-col md="6" v-for="card in cards" :key="card.title">
        <b-card
          class="d-flex cursor-pointer"
          style="border-radius: 16px"
          @click="$router.push(card.link)"
        >
          <div class="d-flex align-items-center justify-content-between">
            <h4 class="text-body font-weight-bolder">{{ card.title }}</h4>
            <b-link :to="card.link" class="text-primary">
              {{ card.linkText }}
              <feather-icon class="ml-1" icon="ArrowRightIcon" size="16" />
            </b-link>
          </div>
          <p class="text-muted">{{ card.description }}</p>
          <div class="d-flex align-items-center mb-1" style="gap: 8px">
            <b-badge
              v-for="(tag, index) in card.tags"
              :key="index"
              :variant="tag.variant"
            >
              {{ tag.label }}
            </b-badge>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="text-body font-weight-bold">Next to Go - Racing</h5>
            <p class="small mb-0">
              Total Hold: $5,920 &nbsp; | &nbsp; Total Liability: $121,500
            </p>
          </div>

          <b-table
            :items="races"
            :fields="fieldsRacing"
            bordered
            small
            responsive
            :busy="loadingNextToGo"
            :thead-class="'text-nowrap'"
          >
            <template #cell(track)="{ item }">
              <b-link
                :to="`/trading/racing/view/${item.meetingId}?raceId=${item.raceId}`"
                >{{ item.venueName }}</b-link
              >
            </template>

            <template #cell(race)="data">
              {{ formatRaceNumber(data.item.raceId) }}
            </template>
            <!-- TIME -->
            <template #cell(time)="data">
              <span> {{ format(new Date(data.item.utcTime), "HH:mm") }}</span>
            </template>

            <template #cell(event)="data">
              <span>{{ data.item.eventName }}</span>
            </template>

            <template #cell(runners)="data">
              <span>{{ data.item.totalRunner }}</span>
            </template>

            <template #cell(fixed)="data">
              <span>{{
                formatCurrencyBuiltInFunc(data.item.totalFixedHold || 0)
              }}</span>
            </template>

            <template #cell(bonus)="data">
              <span>{{
                formatCurrencyBuiltInFunc(data.item.totalFixHoldBonus || 0)
              }}</span>
            </template>

            <template #cell(others)="data">
              <span>{{
                formatCurrencyBuiltInFunc(data.item.totalOthersHold || 0)
              }}</span>
            </template>

            <template #cell(liability)="data">
              <span
                :class="{
                  'text-success': data.item.totalLiability > 0,
                  'text-danger': data.item.totalLiability < 0,
                }"
              >
                {{ formatResult(data.item.totalLiability || 0) }}</span
              >
            </template>

            <!-- TYPE -->
            <template #cell(type)="data">
              <span v-if="!data.item.type">-</span>
              <b-badge
                v-else
                :variant="typeBadgeVariant(data.item.type)"
                class="rounded"
              >
                {{ data.item.type }}
              </b-badge>
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="text-body font-weight-bold">Next to Go - Sports</h5>
            <p class="small mb-0">
              Total Hold: $18,000 &nbsp; | &nbsp; Total Liability: $394,000
            </p>
          </div>

          <b-table
            :items="sports"
            :fields="fieldsSport"
            bordered
            small
            responsive
            :busy="loadingNextToGo"
            :thead-class="'text-nowrap'"
          >
            <!-- Sport badge -->
            <template #cell(sport)="data">
              <b-link to="/trading/sports" class="text-nowrap">{{
                data.item.sport
              }}</b-link>
            </template>

            <template #cell(event)="data">
              <span>{{ data.item.eventTitle }}</span>
            </template>

            <!-- Time -->
            <template #cell(time)="data">
              {{ format(new Date(data.item.startTime), "HH:mm") }}
            </template>

            <template #cell(minutes)="data">
              {{ remainingMinutes(data.item.startTime) }}
            </template>

            <template #cell(fixed)="data">
              <span>{{
                formatCurrencyBuiltInFunc(data.item.totalFixedHold)
              }}</span>
            </template>

            <template #cell(bonus)="data">
              <span>{{
                formatCurrencyBuiltInFunc(data.item.totalFixHoldBonus)
              }}</span>
            </template>

            <template #cell(others)="data">
              <span>{{
                formatCurrencyBuiltInFunc(data.item.totalOthersHold)
              }}</span>
            </template>

            <template #cell(liability)="data">
              <span>{{
                formatCurrencyBuiltInFunc(data.item.totalLiability)
              }}</span>
            </template>

            <template #cell(actions)>
              <b-button size="sm" variant="outline" class="text-primary"
                ><b-link to="/trading/sports">Manage</b-link></b-button
              >
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BContainer,
  BRow,
  BBadge,
  BLink,
  BButton,
  BTable,
} from "bootstrap-vue";
import { mapState, mapActions } from "vuex";
import { addMonths, endOfDay, format, formatDistance } from "date-fns";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";

export default {
  components: {
    BCard,
    BCol,
    BContainer,
    BRow,
    BBadge,
    BLink,
    BButton,
    BTable,
  },
  data() {
    return {
      cards: [
        {
          title: "Racing",
          description:
            "Manage horse racing, greyhound racing, and harness racing events",
          tags: [
            { label: "Thoroughbred", variant: "primary" },
            { label: "Harness", variant: "success" },
            { label: "Greyhound", variant: "info" },
          ],
          link: { name: "trading-racing" },
          linkText: "Access Racing Management",
        },
        {
          title: "Sports",
          description:
            "Manage sports betting across AFL, NRL, Soccer, Basketball and more",
          tags: [
            { label: "AFL/NRL", variant: "warning" },
            { label: "Soccer", variant: "success" },
            { label: "Basketball", variant: "danger" },
          ],
          link: { name: "trading-sports" },
          linkText: "Access Sports Management",
        },
      ],
      fieldsRacing: [
        { key: "track", label: "Track" },
        { key: "race", label: "Race" },
        { key: "time", label: "Time" },
        { key: "type", label: "Type" },
        { key: "event", label: "Event" },
        { key: "runners", label: "Runners" },
        { key: "fixed", label: "Fixed Hold (W/P)" },
        { key: "bonus", label: "Bonus Hold (W/P)" },
        { key: "others", label: "Others Hold" },
        { key: "liability", label: "Liability" },
      ],
      races: [],
      fieldsSport: [
        { key: "sport", label: "Sport" },
        { key: "event", label: "Event" },
        { key: "time", label: "Time" },
        { key: "minutes", label: "Minutes" },
        { key: "fixed", label: "Fixed Hold (W/P)" },
        { key: "bonus", label: "Bonus Hold (W/P)" },
        { key: "others", label: "Others Hold" },
        { key: "liability", label: "Liability" },
        { key: "actions", label: "Actions" },
      ],
      sports: [],
    };
  },
  computed: {
    ...mapState("trading/sport", ["loadingNextToGo"]),
  },
  async created() {
    await Promise.allSettled([
      this.handleFetchNextToGoSport(),
      this.handleFetchNextToGoRacing(),
    ]);
  },
  methods: {
    ...mapActions("trading/sport", ["getNextToGoSport"]),
    ...mapActions("trading/racing", {
      getNextToGoRacing: "getRaceByRangeTime",
    }),
    formatCurrencyBuiltInFunc,
    format,
    async handleFetchNextToGoSport() {
      try {
        const data = await this.getNextToGoSport({
          startTimeFrom: new Date().toISOString(),
          startTimeTo: addMonths(new Date(), 6).toISOString(),
        });
        this.sports = data;
      } catch (e) {
        console.error("Error fetching next to go sports:", e);
      }
    },
    async handleFetchNextToGoRacing() {
      try {
        const now = new Date();
        const data = await this.getNextToGoRacing({
          startDate: now.toISOString(),
          endDate: endOfDay(now, 6).toISOString(),
        });
        this.races = data;
      } catch (e) {
        console.error("Error fetching next to go racing:", e);
      }
    },
    remainingMinutes(startTime) {
      const now = new Date();
      const start = new Date(startTime);
      return formatDistance(now, start) || "-";
    },
    formatRaceNumber(raceId) {
      return raceId.split("-")[1] || "-";
    },
    formatResult(value) {
      if (value > 0) return `+${formatCurrencyBuiltInFunc(value)}`;
      if (value < 0) return `-${formatCurrencyBuiltInFunc(Math.abs(value))}`;
      return formatCurrencyBuiltInFunc(value);
    },
    typeBadgeVariant(type) {
      switch (type) {
        case "T":
          return "primary";
        case "G":
          return "success";
        case "H":
          return "danger";
        default:
          return "secondary";
      }
    },
  },
};
</script>

<style scoped></style>
